import React, { useEffect, useLayoutEffect, useState, lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import LoginPage from '../pages/Login/LoginPage';
import Sites from '../pages/Sites/Sites';
import AppUnifiedHeader from '../components/Layout-Components/Common-Components/Header/AppUnifiedHeader';
import NotFound404 from '../pages/404-Not-Found/NotFound404';
import TrainingPage from '../pages/Training/TrainingPage';
import { useViewerStore } from '../store/store';
import EditorPage from '../pages/Editor/EditorPage';
import { AuthenticatedTemplate, useIsAuthenticated } from '@azure/msal-react';
import PrivateRoutes from './PrivateRoutes';

const BatchAIPage = lazy(() => import('../pages/BatchAI/BatchAIPage'));

/**
 * Handles `/redirect` after auth: send user to `/configure` when `sfidForRedirect` is set,
 * otherwise `/sites`. Must run in one layout effect — a child `<Navigate to="/sites" />` was
 * racing the parent `useEffect` and could leave `sfidForRedirect` uncleared, breaking `/sites`.
 */
const PostAuthSalesforceRedirect: React.FC = () => {
    const isAzureAuthenticated = useIsAuthenticated();
    const navigate = useNavigate();

    useLayoutEffect(() => {
        if (!isAzureAuthenticated) return;

        const sfidForRedirect = sessionStorage.getItem('sfidForRedirect');
        if (sfidForRedirect) {
            if (sessionStorage.getItem('app') === 'oneBUTTON') {
                navigate(`/configure?sfid=${sfidForRedirect}&app=oneBUTTON`, { replace: true });
            } else {
                navigate(`/configure?sfid=${sfidForRedirect}`, { replace: true });
            }
            sessionStorage.removeItem('app');
            sessionStorage.removeItem('sfidForRedirect');
        } else {
            navigate('/sites', { replace: true });
        }
    }, [isAzureAuthenticated, navigate]);

    if (!isAzureAuthenticated) {
        return <LoginPage />;
    }

    return null;
};

const LoginRoute: React.FC<{ isAzureAuthenticated: boolean }> = ({ isAzureAuthenticated }) => {
    if (!isAzureAuthenticated) return <LoginPage />;
    const postLoginRedirectPath = sessionStorage.getItem('postLoginRedirectPath');
    if (postLoginRedirectPath) {
        sessionStorage.removeItem('postLoginRedirectPath');
        return <Navigate replace to={postLoginRedirectPath} />;
    }
    return <Navigate replace to='/sites' />;
};

const AppRoutes: React.FC = () => {
    const isAzureAuthenticated = useIsAuthenticated();
    const location = useLocation();
    const [hideHeader, setHideHeader] = useState<boolean>(false);

    useEffect(() => {
        if (location.search.includes('sfid') && !isAzureAuthenticated) {
            const urlParams = new URLSearchParams(location.search);
            const sfid = urlParams.get('sfid');
            if (sfid) sessionStorage.setItem('sfidForRedirect', sfid);
            if (location.search.includes('app=oneBUTTON')) {
                sessionStorage.setItem('app', 'oneBUTTON');
            }
        }

        if (window.location.search.includes('app=oneBUTTON')) {
            setHideHeader(true);
            useViewerStore.setState({ isIframeEmbeddedInOneButton: 'true' });
        }
    }, [location, isAzureAuthenticated]);

    const isSiteRedirectNeeded =
        location.pathname === '/sites' && !!sessionStorage.getItem('sfidForRedirect');

    return (
        <>
            <AuthenticatedTemplate>{!hideHeader && <AppUnifiedHeader />}</AuthenticatedTemplate>

            <Routes>
                <Route path='/' element={<Navigate replace to='/login' />} />

                <Route
                    path='/login'
                    element={<LoginRoute isAzureAuthenticated={isAzureAuthenticated} />}
                />

                <Route element={<PrivateRoutes />}>
                    <Route
                        path='/sites'
                        element={
                            isSiteRedirectNeeded ? <Navigate replace to='/redirect' /> : <Sites />
                        }
                    />
                    <Route path='/redirect' element={<PostAuthSalesforceRedirect />} />
                    <Route path='/training' element={<TrainingPage />} />
                    <Route path='/configure/*' element={<EditorPage />} />
                    <Route path='/batch-ai' element={<Suspense fallback={<div style={{ padding: 40, color: '#fff' }}>Loading...</div>}><BatchAIPage /></Suspense>} />

                    {!location.pathname.includes('/redirect') && (
                        <Route path='*' element={<NotFound404 />} />
                    )}
                </Route>
            </Routes>
        </>
    );
};

export default AppRoutes;
